import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Upload, FileJson, History } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const RATING_COLOR = { A: "bg-green-700", B: "bg-green-500", C: "bg-amber-500", D: "bg-orange-600", E: "bg-red-600" };
const fmt = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT") : "—");

function Pillar({ k, v }) {
  return (
    <div data-testid={`dd-pillar-${k}`}>
      <div className="flex justify-between text-xs font-semibold text-slate-600"><span>{k === "E" ? "Ambiente (E)" : k === "S" ? "Sociale (S)" : "Governance (G)"}</span><span>{v}/100</span></div>
      <div className="h-2 rounded-full bg-slate-100 mt-1"><div className="h-full rounded-full bg-green-700" style={{ width: `${v}%` }} /></div>
    </div>
  );
}

function AssessmentCard({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="dd-assessment-card">
      <div className="flex flex-wrap items-center gap-4">
        <span className={`w-12 h-12 rounded-lg text-white text-2xl font-extrabold grid place-items-center ${RATING_COLOR[a.rating]}`} data-testid="dd-rating">{a.rating}</span>
        <div className="flex-1 min-w-[160px]">
          <p className="text-xs font-semibold text-slate-500 uppercase">{a.tipo} · {fmt(a.data)}</p>
          <p className="text-2xl font-extrabold text-slate-900" data-testid="dd-score">{a.punteggio}<span className="text-sm text-slate-400 font-normal">/100</span></p>
          <p className="text-xs text-slate-500">Fonte: {a.fonte}</p>
        </div>
        <div className="w-full sm:w-64 space-y-2">{["E", "S", "G"].map((k) => <Pillar key={k} k={k} v={a.pilastri[k]} />)}</div>
      </div>
      {a.indicatori?.length > 0 && (
        <button type="button" onClick={() => setOpen(!open)} className="mt-3 text-xs font-semibold text-green-800 underline" data-testid="dd-toggle-indicators">
          {open ? "Nascondi" : "Mostra"} {a.indicatori.length} indicatori
        </button>
      )}
      {open && (
        <table className="w-full text-xs mt-3" data-testid="dd-indicators-table">
          <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Codice</th><th>Pilastro</th><th>Indicatore</th><th>Punteggio</th><th>Peso</th></tr></thead>
          <tbody>{a.indicatori.map((i) => (
            <tr key={i.codice} className="border-b border-slate-100">
              <td className="py-1.5 font-mono">{i.codice}</td><td>{i.pilastro}</td>
              <td>{i.indicatore} {i.da_jobtimehr && <span className="ml-1 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-bold" data-testid="dd-jobtimehr-badge">da JobTimeHR</span>}</td>
              <td>{i.punteggio}/4</td><td>{i.peso}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

export function DueDiligencePanel() {
  const { companyId, period, company } = useTenant();
  const [items, setItems] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState(null);
  const [askCreate, setAskCreate] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/dd/assessments`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await api.get(`/companies/${companyId}/dd/export.csv`, { params: { period_id: period?.id }, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "dd_esg.csv";
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Esportato ${name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doImport = async (createIfMissing = false) => {
    let esito;
    try { esito = JSON.parse(text); } catch { setError("JSON non valido: sintassi errata (controlla virgole e parentesi)."); return; }
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/dd/esito/import", { esito, create_if_missing: createIfMissing });
      toast.success(`Esito importato per ${data.company.denominazione}`);
      setShowImport(false); setText(""); setAskCreate(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 404 && d?.code === "COMPANY_NOT_FOUND") setAskCreate(d);
      else setError(typeof d === "string" ? d : formatApiError(e));
    } finally { setBusy(false); }
  };

  const onFile = (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    f.text().then((t) => { setText(t); setError(null); });
  };

  return (
    <Card className="border-slate-200" data-testid="dd-panel">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">Due Diligence ESG</CardTitle>
          <div className="flex gap-2">
            <button type="button" onClick={exportCsv} className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50" data-testid="dd-export-button">
              <Download className="w-4 h-4" aria-hidden="true" /> Esporta per Due Diligence
            </button>
            <button type="button" onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md bg-green-700 text-white hover:bg-green-800" data-testid="dd-import-button">
              <Upload className="w-4 h-4" aria-hidden="true" /> Importa esito Due Diligence
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="dd-empty">Nessuna valutazione da Due Diligence importata per {company?.denominazione || "questa azienda"}.</p>
        ) : (
          <>
            <AssessmentCard a={items[0]} />
            {items.length > 1 && (
              <div data-testid="dd-history">
                <p className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-1 mt-4 mb-2"><History className="w-3.5 h-3.5" aria-hidden="true" /> Storico valutazioni</p>
                <ul className="divide-y divide-slate-100 text-sm">
                  {items.slice(1).map((a) => (
                    <li key={a.id} className="py-2 flex items-center gap-3">
                      <span className={`w-7 h-7 rounded text-white text-sm font-bold grid place-items-center ${RATING_COLOR[a.rating]}`}>{a.rating}</span>
                      <span className="font-semibold">{a.punteggio}/100</span>
                      <span className="text-slate-500">{fmt(a.data)} · E {a.pilastri.E} · S {a.pilastri.S} · G {a.pilastri.G}</span>
                    </li>))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={showImport} onOpenChange={(o) => { setShowImport(o); if (!o) { setError(null); setAskCreate(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="dd-import-dialog">
          <DialogHeader><DialogTitle>Importa esito Due Diligence</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Incolla il JSON ricevuto dall'app di Due Diligence oppure carica il file. L'azienda viene abbinata tramite partita IVA.</p>
          <Textarea value={text} onChange={(e) => { setText(e.target.value); setError(null); setAskCreate(null); }} rows={10}
            placeholder='{"fonte": "Due Diligence PMI", "azienda": "...", "partita_iva": "...", "data": "2026-01-31", "rating": "B", "punteggio": 72, "pilastri": {"E": 70, "S": 75, "G": 71}, "indicatori": []}'
            className="font-mono text-xs" data-testid="dd-import-textarea" />
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".json,application/json" onChange={onFile} className="hidden" data-testid="dd-import-file" />
            <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-md border border-slate-300" data-testid="dd-import-file-button">
              <FileJson className="w-4 h-4" aria-hidden="true" /> Carica file JSON
            </button>
            <button type="button" onClick={() => doImport(false)} disabled={busy || !text.trim()} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-md bg-green-700 text-white disabled:opacity-50" data-testid="dd-import-submit">
              {busy ? "Importazione…" : "Importa"}
            </button>
          </div>
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="dd-import-error">{error}</p>}
          {askCreate && (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3 space-y-2" data-testid="dd-ask-create">
              <p>{askCreate.message}</p>
              {askCreate.can_create ? (
                <div className="flex gap-2">
                  <button type="button" onClick={() => doImport(true)} disabled={busy} className="text-sm font-semibold px-3 py-1.5 rounded-md bg-amber-600 text-white" data-testid="dd-create-confirm">Crea l'azienda e importa</button>
                  <button type="button" onClick={() => setAskCreate(null)} className="text-sm font-semibold px-3 py-1.5 text-slate-600" data-testid="dd-create-cancel">Annulla</button>
                </div>
              ) : <p className="text-slate-600">Chiedi al consulente di creare l'azienda prima di importare.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
